import {Component, Inject} from '@angular/core';
import {BaseModalComponent} from "app/shared/base-modal/base-modal.component";
import {NameTranslationType, Product, RelatedProductsInfo} from "app/models/menu.model";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {UntilDestroy} from "@ngneat/until-destroy";
import {Organization} from "app/models/organization.model";
import {FormControl, FormGroup} from "@angular/forms";
import {
  SelectProductsModalComponent,
  SelectProductsModalComponentDataType
} from "app/pages/menu/shared/select-products-modal/select-products-modal.component";
import {TranslateService} from "@ngx-translate/core";

@UntilDestroy()
@Component({
  selector: 'app-edit-related-products-modal',
  templateUrl: './edit-related-products-modal.component.html',
  styleUrls: ['./edit-related-products-modal.component.scss']
})
export class EditRelatedProductsModalComponent extends BaseModalComponent {
  initValues = {}
  form: FormGroup;
  relatedProductIds: string[] = [];
  relatedProductsNames = new Set<string>();

  constructor(
    private translate: TranslateService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<EditRelatedProductsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditRelatedProductsData
  ) {
    super(dialogRef);

    for (const relatedProductInfo of this.data.relatedProductInfos) {
      if(this.data.relatedProductInfo.name !== relatedProductInfo.name){
        this.relatedProductsNames.add(relatedProductInfo.name);
      }
    }

    this.form = new FormGroup({});
    this.initValues["relatedProductName"] = data.relatedProductInfo.name;
    this.form.addControl('relatedProductName', new FormControl(data.relatedProductInfo.name ?? ''));

    if(!data.relatedProductInfo.nameTranslations){
      data.relatedProductInfo.nameTranslations = {} as NameTranslationType;
    }

    for(const lang of this.data.organization.additionalLanguages){
      this.initValues[lang] = data.relatedProductInfo.nameTranslations[lang] ?? null;
      this.form.addControl(lang, new FormControl(data.relatedProductInfo.nameTranslations[lang] ?? ''))
    }

    if(data.relatedProductInfo.products){
      this.relatedProductIds = [...data.relatedProductInfo.products]
    }
  }

  submit(){
    if(!this.validate()){
      return;
    }
    const translations: NameTranslationType = {};
    for (const lang of this.data.organization.additionalLanguages) {
      translations[ lang ] = this.form.value[ lang ];
    }

    const relatedProductsInfo = {
      name: this.form.value['relatedProductName'],
      nameTranslations: translations,
      products: this.relatedProductIds,
      legacy: this.data.relatedProductInfo.legacy,
    } as RelatedProductsInfo;
    if(this.data.isNew){
      this.exit({created: relatedProductsInfo});
    } else {
      this.exit({updated: relatedProductsInfo});
    }
  }

  validate(){
    if(this.relatedProductIds.length == 0){
      this.form.setErrors({'relatedProductIds': "Can't be empty"});
      return false;
    }

    if(!this.form.value['relatedProductName']){
      this.form.setErrors({'relatedProductName': "Name is required"});
      return false;
    }

    if(this.relatedProductsNames.has(this.form.value['relatedProductName'])){
      this.form.setErrors({'relatedProductName': 'Should be unique'});
      return false;
    }

    return true;
  }

  delete() {
    this.exit({deleted: this.data.relatedProductInfo})
  }

  editRelatedProducts() {
    const productIds = this.relatedProductIds ?? [];

    this.dialog.open(SelectProductsModalComponent, {
      data: {
        title: this.translate.instant('MENU.DISHES.NEW.SELECT_RELATED_PRODUCTS', {productName: this.data.product.name}),
        productIds: productIds,
      } as SelectProductsModalComponentDataType
    }).afterClosed().subscribe(async res => {
      if (res) {
        const newProductIds = res.data;
        this.relatedProductIds = [...newProductIds];
      }
    });
  }
}

export interface EditRelatedProductsData {
  relatedProductInfo: RelatedProductsInfo,
  organization: Organization,
  products: Map<string, Product>,
  product: Product,
  relatedProductInfos: RelatedProductsInfo[],
  isNew: boolean,
}
